import { defineStore } from 'pinia'
import { useApi } from '@/api/jsonapi'
import type { SemesterResource } from '@/api/contract'
import { readStored, writeStored } from '@/utils/storage'

type Attributes = SemesterResource['attributes']

export interface SemesterTab {
    code: string
    slot: Attributes['slot']
    season: Attributes['season']
    nameCs: string
    nameEn: string
}

/** The three semester tabs as the API marks them; the client never works out "current" itself (spec §4.5). */
export const useSemesters = defineStore('semesters', {
    state: () => ({ tabs: [] as SemesterTab[], selected: null as string | null }),
    getters: {
        current: (state): string | null => state.tabs.find((tab) => tab.slot === 'current')?.code ?? null,
    },
    actions: {
        async load() {
            const document = await useApi().semesters.self()
            this.tabs = document.data.map((semester) => ({
                code: semester.id,
                slot: semester.attributes.slot,
                season: semester.attributes.season,
                nameCs: semester.attributes.nameCs,
                nameEn: semester.attributes.nameEn,
            }))
            this.resolve(this.selected ?? readStored<string | null>('harm.semester', null, (value) => typeof value === 'string'))
        },
        /** A code that is not one of the three tabs (an old bookmark) falls back to the current semester. Not remembered. */
        resolve(code: string | null) {
            this.selected = this.tabs.some((tab) => tab.code === code) ? code : this.current
        },
        /** The user's choice (a tab click or a shared link): only a real tab is remembered, so the default never sticks. */
        select(code: string | null) {
            this.resolve(code)
            if (this.selected !== null && this.selected === code) {
                writeStored('harm.semester', this.selected)
            }
        },
    },
})
